import URI from 'urijs';
import IWindow from '@bingads-webui-clientcenter/window-interface';
import { separator } from './url-builder';
import type { UriInstance, UrlQueryParams, UrlQueryValue } from './types';

const rurlParamName = 'rurl';

/**
 * Get the terms and conditions url
 * @param {string} ccBaseUrl - base Client Center url
 * @return {string} the Terms and condition url
 */
export const getTermsAndConditionsUrl = (ccBaseUrl: string) => new URI(ccBaseUrl).segment(['TermsAndConditions', 'Index']).toString();

/**
 * Gets a Forward link Url
 * @param {string} fwLinkBaseUrl - the baseURl for the fwlink
 * @param {string} linkid - the linkId for the fwlink
 * @return {string} the path string to the forward link
 */
export const getForwardLink = (fwLinkBaseUrl: string, linkid: string | number) => new URI(fwLinkBaseUrl).addSearch({ linkid }).toString();

/**
 * Gets a Campaign page url
 * @param {string} campaignAzureDomain - the baseURl for the campaign azure domain
 * @param {string} pathname - the path name for the campaign page
 * @param {string} searchParams - the search params for the campaign page
 * @return {string} the path string to the campaign page
 */
export const getCampaignPageUrl = (campaignAzureDomain: string, pathname: string, searchParams?: UrlQueryParams) => new URI(campaignAzureDomain).segment(['campaign', 'vnext', pathname]).addSearch({ ...searchParams }).toString();

/**
 * Get the current path in the browser
 * @return {string} the current path string
 */
export const getCurrentPath = () => new URI(IWindow.location.href).path();

/**
 * Append a redirect url to the uri
 * @param {URI} uri - the uri to add redirect url
 * @param {string} rurl - redirect url to add
 * @return {URI} the uri which contains the redirect url
 */
export const appendRedirectUrl = (uri: UriInstance, rurl: string) => uri.addSearch(rurlParamName, rurl);

/**
 * Get the error(Panda) page url
 * @param {string} ccBaseUrl ccui's base url.
 * @param {string} requestId The request Id of the error request.
 * @param {string} previousUrl The previousurl which is the url when error happened.
 * @return {string} The error page's url.
 */
export const getErrorPageUrl = (ccBaseUrl: string, requestId: string | number, previousUrl: string) => new URI(ccBaseUrl).segment(['Error']).setSearch({ reqid: requestId, previousurl: previousUrl }).toString();

/**
 * Get the SAPApplication supporting document url.
 * @param {string} ccBaseUrl ccui's base url.
 * @param {(number|string)} cid the customer id.
 * @param {(number|string)} uid the user id.
 * @param {(number|string)} SAPApplicationId the SAPApplication Id.
 * @param {string} fileName fileName of the document.
 * @returns {string} the SAPApplication supporting document url.
 */
export const getSAPApplicationSupportingDocumentUrl = (ccBaseUrl: string, cid: UrlQueryValue, uid: UrlQueryValue, SAPApplicationId: UrlQueryValue, fileName: string) =>
  new URI(ccBaseUrl).segment(['API', 'PaymentInstrument', 'SAPApplicationSupportingDocument'].join(separator))
    .setSearch({
      fileName, SAPApplicationId, cid, uid,
    }).toString();

/**
 * Append a query to the uri hash
 * @param {URI} uri - the uri to add hash query
 * @param {string} hashQuery - query to add to hash
 * @return {URI} the uri whose hash contains the query
 */
export const appendHashQuery = (uri: UriInstance, hashQuery: string) => uri.hash(URI(uri.fragment()).segment(hashQuery) as unknown as string);

/**
 * Get the SAPApplication supporting document url.
 * @param {string} ccBaseUrl - ccui's base url.
 * @param {(number|string)} cid - the customer id.
 * @param {(number|string)} uid - the user id.
 * @param {(number|string)} accountId - the tax certification account owner id.
 * @param {string} blobContainerName - blob container name of the tax certification.
 * @param {string} certificationName - the tax certification name.
 * @returns {string} the account tax certification url.
 */
export const getAccountTaxCertificationUrl = (ccBaseUrl: string, cid: UrlQueryValue, uid: UrlQueryValue, accountId: UrlQueryValue, blobContainerName: string, certificationName: string) =>
  new URI(ccBaseUrl).segment(['API', 'Account', 'AccountTaxCertification'].join(separator))
    .setSearch({
      accountId, blobContainerName, certificationName, cid, uid,
    }).toString();
